import summaryCommonStore from "@/store/summaryCommon.store";
import { useEffect, useRef } from "react";
import { shallow } from "zustand/shallow";
import { List } from "react-virtualized";
import { getParaInfo } from "@/bussiness/RecordHelper/utils";
import avPlayerStore from "@/store/avPlayer.store";
import { IPG } from "@/types/summary";
import findLastIndex from "@/utils/findLastIndex";

const useOriginTextFollow = (params: {
  records: IPG[];
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  virtualListComRef?: React.RefObject<List>;
}) => {
  const { records, scrollContainerRef, virtualListComRef } = params;
  const firstFireImmediately = useRef(true);
  const formatNewRecords = records.map((item) => {
    const paraInfo = getParaInfo(item);
    const { bt, et } = paraInfo;
    item.bt = bt;
    item.et = et;
    return item;
  });
  const listRef = useRef(formatNewRecords);
  listRef.current = formatNewRecords;

  const rowsRenderedInfo = useRef({
    startIndex: 0,
    stopIndex: 0,
  });

  useEffect(() => {
    let playingIndex = 0;
    let paragraph: HTMLDivElement | null = null;
    let shoudScroll = true;

    function setPlayingParagraph(playingTime: number) {
      let index = listRef.current.findIndex((item, currentIndex) => {
        const { bt, et } = item;
        let nextBt;
        const nextItem = listRef.current[currentIndex + 1];
        if (nextItem) {
          const { bt } = nextItem;
          nextBt = bt;
        }
        return (
          playingTime >= bt! / 1000 &&
          (nextBt ? playingTime < nextBt! / 1000 : playingTime <= et! / 1000)
        );
      });

      // 没有找到完美时间点, 放大区域
      if (index === -1) {
        index = listRef.current.findIndex((item) => {
          return playingTime <= item.et! / 1000;
        });
      }
      // 没有找到完美时间点, 放大区域
      if (index === -1) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        index = findLastIndex(listRef.current, (item: any) => {
          return playingTime >= item.bt! / 1000;
        });
      }

      if (index === -1) return { targetItem: null, index: -1 };
      // if (playingIndex !== index) {
      //   console.log("setPlayingParagraph", shoudScroll, playingTime, index);
      // }

      playingIndex = index;
      
      // 安全访问listRef.current[index]，防止undefined错误
      const currentItem = listRef.current?.[index];
      if (!currentItem?.pi) {
        console.warn(`setPlayingParagraph: listRef.current[${index}] 或其 pi 属性为空`);
        return { targetItem: null, index: -1 };
      }
      
      avPlayerStore.getState().updatePlayingPi(currentItem.pi);
      const targetItem = scrollContainerRef.current?.querySelector(
        `[data-p-id="${currentItem.pi}"]`
      );
      paragraph = targetItem as HTMLDivElement;
      setPlayingSentence(playingTime);
      return { targetItem, index };
    }

    function setPlayingSentence(playingTime: number) {
      const playingRecord = listRef.current[playingIndex];
      let index = 0;
      index = playingRecord.sc.findIndex((sItem) => {
        const { bt: sentBt, et: sentEt } = sItem;
        return sentBt <= playingTime * 1000 && sentEt > playingTime * 1000;
      });
      if (index === -1) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        index = findLastIndex(playingRecord.sc, (sItem: any) => {
          const { bt: sentBt } = sItem;
          return sentBt <= playingTime * 1000;
        });
      }
      if (index === -1) {
        index = playingRecord.sc.findIndex((sItem) => {
          const { et: sentEt } = sItem;
          return sentEt >= playingTime * 1000;
        });
      }
      const si = index === -1 ? "" : playingRecord.sc[index].si;
      avPlayerStore.getState().updatePlayingSi(si);
    }

    async function scrollIntoView(index: number) {
      if (virtualListComRef) {
        await jumpToIndex(index);
      }

      if (!paragraph && index !== -1) {
        // 安全访问listRef.current[index]，防止undefined错误
        const currentItem = listRef.current?.[index];
        if (currentItem?.pi) {
          const targetItem = scrollContainerRef.current?.querySelector(
            `[data-p-id="${currentItem.pi}"]`
          );
          paragraph = targetItem as HTMLDivElement;
        } else {
          console.warn(`scrollIntoView: listRef.current[${index}] 或其 pi 属性为空`);
        }
      }
      // console.log("scrollIntoView", index, paragraph);

      const content = paragraph?.querySelector("#content");
      if (!content) return;
      content.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "start",
      });
    }

    const modeUnsubscribe = summaryCommonStore.subscribe(
      (state) => ({
        isScrolled: state.isScrolled,
      }),
      (state, prevState) => {
        shoudScroll = !state.isScrolled;
        if (prevState.isScrolled && !state.isScrolled) {
          scrollIntoView(playingIndex);
        }
      },
      {
        equalityFn: shallow,
        fireImmediately: false,
      }
    );

    const unsubscribe = avPlayerStore.subscribe(
      (state) => state.playingTime,
      (playingTime) => {
        if (firstFireImmediately.current && playingTime === 0) return;
        firstFireImmediately.current = false;
        const useFirst = Math.floor(playingTime) === 0;
        // 如果当前不存在缓存的段落(或者从头播放时)，查找当前播放时间所在的段落
        if (useFirst) {
          scrollIntoView(0);
        }

        setPlayingParagraph(playingTime);
        if (playingIndex === -1) return;
        shoudScroll && scrollIntoView(playingIndex);
      },
      {
        fireImmediately: false,
      }
    );
    return () => {
      paragraph = null;
      unsubscribe();
      modeUnsubscribe();
    };
  }, []);

  const jumpToIndex = async (pIndex: number) => {
    let attemptCount = 0; // 每次调用重置计数器
    const MAX_ATTEMPTS = 2;

    // eslint-disable-next-line no-constant-condition
    while (attemptCount < MAX_ATTEMPTS) {
        // 安全访问listRef.current[pIndex]，防止undefined错误
        const currentItem = listRef.current?.[pIndex];
        if (!currentItem?.pi) {
            console.warn(`useOriginTextFollow: listRef.current[${pIndex}] 或其 pi 属性为空`);
            return false; // 如果没有有效的pi，直接返回false
        }

        const isVisible = !!scrollContainerRef.current?.querySelector(
          `[data-p-id="${currentItem.pi}"]`
        );
        
        if (isVisible) {
            return true; // 成功可见时立即返回
        }

        if (virtualListComRef?.current) {
            try {
                await virtualListComRef.current.scrollToRow(pIndex);
            } catch (error) {
                console.error('Scroll failed:', error);
            }
        }

        attemptCount++;
        await new Promise(resolve => setTimeout(resolve, 250)); // 增加重试间隔
    }

    console.warn(`Failed after ${MAX_ATTEMPTS} attempts`);
    return false; // 明确返回尝试结果
};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onRowsRendered = (info: any) => {
    rowsRenderedInfo.current = info;
  };

  return { onRowsRendered, jumpToIndex };
};
export default useOriginTextFollow;
